import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Inject,
  signal,
  Signal,
} from '@angular/core';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';

import { SecurityFeatureModel, SecurityService } from '@ups/security';
import { SecurityConstants } from '@ups/security-constants';
import {
  HelpActions,
  IAnchorRegistry,
  IWalkthroughNavigator,
  IWalkthroughRenderer,
  WALKTHROUGH_ANCHOR_REGISTRY,
  WALKTHROUGH_NAVIGATOR,
  WALKTHROUGH_RENDERER,
  WalkthroughDocumentStepDto,
  WalkthroughPlayerBaseComponent,
} from '@ups/xplat/base/in-app-help';

import { WalkthroughAudioService } from '../../services/walkthrough-audio.service';
import { deriveWalkthroughNarration } from '../../services/walkthrough-narration.util';

/**
 * Web walkthrough player. The tour state machine lives in
 * {@link WalkthroughPlayerBaseComponent}; this subclass adds only what is
 * web-specific: the Kendo intro ("Begin Walkthrough") and incomplete-tour
 * dialogs in its template, the Software Security gating those dialogs read,
 * spoken step narration, and the hand-off to the in-app walkthrough editor.
 */
@Component({
  selector: 'ups-walkthrough-player',
  templateUrl: './walkthrough.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WalkthroughPlayerComponent extends WalkthroughPlayerBaseComponent<HTMLElement> {
  public securityConsts = SecurityConstants;

  /**
   * The walkthroughs feature grants, as a signal. SecurityService keeps them in a
   * plain cache that fills asynchronously after sign-in (and refills on a profile
   * update), so OnPush cannot read it straight from the template: each
   * `permissionsChanged$` emission bumps `permissionTick`, which this computed
   * reads, so the intro dialog's Edit/Begin buttons appear when the grants land.
   */
  readonly walkthroughFeature: Signal<SecurityFeatureModel>;
  private readonly permissionTick = signal(0);

  constructor(
    store: Store,
    public security: SecurityService,
    actions$: Actions,
    @Inject(WALKTHROUGH_RENDERER) renderer: IWalkthroughRenderer<HTMLElement>,
    @Inject(WALKTHROUGH_ANCHOR_REGISTRY) anchors: IAnchorRegistry<HTMLElement>,
    @Inject(WALKTHROUGH_NAVIGATOR) navigator: IWalkthroughNavigator,
    public audioService: WalkthroughAudioService
  ) {
    super(store, actions$, renderer, anchors, navigator);
    security.permissionsChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.permissionTick.update((n) => n + 1));
    this.walkthroughFeature = computed(() => {
      this.permissionTick();
      return security.getFeatureById(
        SecurityConstants.employee_portal_frameworkfeatures_walkthroughs
      );
    });
  }

  /**
   * Speaks the step that just opened. Narration text is derived from the step's own
   * title + content (phase 1 has no authored narration field). Fire-and-forget: audio
   * is an enhancement and the audio service swallows failures so the tour is never
   * blocked.
   */
  protected override onStepShown(
    step: WalkthroughDocumentStepDto,
    idx: number
  ): void {
    // `audioService?` is defence in depth (WI 31438). The base now subscribes in
    // ngOnInit (WI 31548), so this override no longer runs during super(); the guard
    // stays so a future constructor-time call cannot crash the player again. No audio
    // can be live before init, so skipping the call then is correct.
    void this.audioService?.playForStep(
      this.walkthrough()?.id,
      idx,
      deriveWalkthroughNarration(step)
    );
  }

  /** Single stop choke point: runs on every step change AND on teardown (end/close/give-up). */
  override clearHighlight(): void {
    // `audioService?` is defence in depth, as in onStepShown (WI 31438 / 31548).
    // super.clearHighlight() is always safe; only the audio stop needs the guard.
    this.audioService?.stop();
    super.clearHighlight();
  }

  edit(): void {
    this.store.dispatch(
      HelpActions.toggleEditor({ state: true, document: this.walkthrough()! })
    );
    this.end();
  }
}
